import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useI18n, formatDateTime } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChefHat, Clock, Maximize2, Volume2, VolumeX } from 'lucide-react';
import { cn } from '@/lib/utils';

function elapsedMinutes(iso) {
  if (!iso) return 0;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
}

export default function Kds() {
  const { t, lang } = useI18n();
  const [orders, setOrders] = useState([]);
  const [tick, setTick] = useState(0);
  const [muted, setMuted] = useState(false);
  const [lastCount, setLastCount] = useState(0);

  const load = async () => {
    const r = await api.get('/orders', { params: { kitchen: true } });
    setOrders(r.data);
    if (!muted && r.data.length > lastCount && lastCount > 0) {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.frequency.value = 880;
        g.gain.value = 0.05;
        o.start();
        setTimeout(() => o.stop(), 200);
      } catch (audioErr) {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('KDS audio beep skipped:', audioErr?.message || audioErr);
        }
      }
    }
    setLastCount(r.data.length);
  };

  useEffect(() => {
    load();
    const int = setInterval(() => {
      load();
      setTick(t => t + 1);
    }, 5000);
    return () => clearInterval(int);
  }, []);

  const updateItem = async (orderId, itemId, estado) => {
    await api.put(`/orders/${orderId}/items/${itemId}`, { estado });
    load();
  };

  const updateOrderStatus = async (orderId, estado) => {
    await api.put(`/orders/${orderId}/status`, { estado });
    load();
  };

  const goFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      document.documentElement.requestFullscreen?.();
    }
  };

  // Group orders by KDS column based on item-level statuses
  const columns = {
    nuevo: [],
    preparando: [],
    listo: [],
  };
  orders.forEach(o => {
    const items = o.items || [];
    const hasNew = items.some(it => it.estado === 'nuevo' || it.estado === 'preparando');
    const allReady = items.length > 0 && items.every(it => it.estado === 'listo' || it.estado === 'servido');
    if (allReady) {
      columns.listo.push(o);
    } else if (o.estado === 'enviado_cocina') {
      columns.nuevo.push(o);
    } else {
      columns.preparando.push(o);
    }
  });

  return (
    <div className="min-h-screen bg-[hsl(210_18%_8%)] text-white kds-mode" data-testid="kds-page">
      <div className="sticky top-0 z-20 px-4 py-3 border-b border-[hsl(210_14%_18%)] bg-[hsl(210_18%_10%)] flex items-center gap-3">
        <ChefHat className="h-5 w-5 text-[hsl(43_92%_55%)]" />
        <h1 className="font-heading font-bold text-lg">{t('nav_kds')}</h1>
        <div className="text-xs text-[hsl(210_10%_70%)] hidden sm:block">
          {orders.length} {lang === 'es' ? 'pedidos activos' : 'pedidos ativos'}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="font-mono text-sm tabular-nums">{new Date().toLocaleTimeString(lang === 'pt' ? 'pt-BR' : 'es-ES', { hour: '2-digit', minute: '2-digit' })}</span>
          <Button variant="ghost" size="icon" onClick={() => setMuted(m => !m)} className="text-white hover:bg-white/10" data-testid="kds-mute-button">
            {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </Button>
          <Button variant="ghost" size="icon" onClick={goFullscreen} className="text-white hover:bg-white/10" data-testid="kds-fullscreen-button">
            <Maximize2 className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => window.history.back()} className="border-white/20 text-white hover:bg-white/10" data-testid="kds-back-button">
            ←
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3">
        {[
          { key: 'nuevo', title: t('kds_status_new'), color: 'hsl(43 92% 55%)', orders: columns.nuevo },
          { key: 'preparando', title: t('kds_status_preparing'), color: 'hsl(199 70% 55%)', orders: columns.preparando },
          { key: 'listo', title: t('kds_status_ready'), color: 'hsl(142 70% 45%)', orders: columns.listo },
        ].map((col) => (
          <div key={col.key} className="flex flex-col">
            <div className="flex items-center justify-between px-3 py-2 mb-2 sticky top-14">
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: col.color }}></span>
                <span className="font-heading font-bold uppercase text-sm tracking-wider">{col.title}</span>
              </div>
              <Badge className="bg-white/10 text-white border-white/20 hover:bg-white/10">{col.orders.length}</Badge>
            </div>
            <div className="space-y-3">
              {col.orders.length === 0 ? (
                <div className="text-center py-12 text-sm text-[hsl(210_10%_50%)]">
                  {lang === 'es' ? 'Sin pedidos' : 'Sem pedidos'}
                </div>
              ) : col.orders.map((o) => {
                const elapsed = elapsedMinutes(o.created_at);
                const urgent = elapsed > 20;
                return (
                  <div
                    key={o.id}
                    data-testid={`kds-ticket-${o.numero}`}
                    className={cn(
                      'rounded-xl border bg-[hsl(210_18%_12%)] border-[hsl(210_14%_22%)] overflow-hidden',
                      urgent && 'border-l-4 border-l-[hsl(0_84%_60%)]'
                    )}
                  >
                    <div className="px-3 py-2 border-b border-[hsl(210_14%_22%)] flex items-center justify-between">
                      <div>
                        <div className="font-heading font-bold">
                          {o.tipo === 'mesa' ? `${t('tables')} ${o.table_numero}` : o.tipo}
                          {' • #'}{o.numero}
                        </div>
                        <div className="text-xs text-[hsl(210_10%_70%)]">
                          {o.comensales} {t('diners')} • {formatDateTime(o.created_at, lang)}
                        </div>
                      </div>
                      <div className={cn(
                        'flex items-center gap-1 font-mono text-sm tabular-nums px-2 py-1 rounded-md',
                        urgent ? 'bg-[hsl(0_84%_60%)] text-white animate-pulse' : 'bg-white/10'
                      )}>
                        <Clock className="h-3.5 w-3.5" />
                        {elapsed}m
                      </div>
                    </div>
                    <div className="p-3 space-y-1.5">
                      {(o.items || []).map((it) => (
                        <div key={it.id} className="flex items-start gap-2 text-sm">
                          <span className="font-bold text-[hsl(43_92%_55%)] font-mono w-6 text-right shrink-0">{it.cantidad}x</span>
                          <div className="flex-1 min-w-0">
                            <div className={cn(
                              'font-medium leading-tight',
                              it.estado === 'listo' && 'line-through opacity-50',
                            )}>
                              {it.nombre}
                            </div>
                            {it.notas && <div className="text-xs italic text-[hsl(210_10%_70%)]">{it.notas}</div>}
                          </div>
                          {col.key !== 'listo' && (
                            <button
                              onClick={() => updateItem(o.id, it.id, it.estado === 'preparando' ? 'listo' : 'preparando')}
                              className="text-xs px-2 py-1 rounded-md bg-white/10 hover:bg-white/20"
                              data-testid={`kds-item-${it.id}-toggle`}
                            >
                              {it.estado === 'preparando' ? t('mark_ready') : t('mark_preparing')}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="px-3 pb-3 flex gap-2">
                      {col.key === 'nuevo' && (
                        <Button
                          size="sm"
                          className="flex-1 bg-[hsl(199_70%_55%)] hover:bg-[hsl(199_70%_45%)] text-white"
                          onClick={() => updateOrderStatus(o.id, 'en_preparacion')}
                          data-testid={`kds-order-${o.numero}-start`}
                        >
                          {t('mark_preparing')}
                        </Button>
                      )}
                      {col.key === 'preparando' && (
                        <Button
                          size="sm"
                          className="flex-1 bg-[hsl(142_70%_45%)] hover:bg-[hsl(142_70%_35%)] text-white"
                          onClick={async () => {
                            // mark all items ready
                            for (const it of (o.items || [])) {
                              if (it.estado !== 'listo' && it.estado !== 'servido' && it.estado !== 'cancelado') {
                                await api.put(`/orders/${o.id}/items/${it.id}`, { estado: 'listo' });
                              }
                            }
                            await api.put(`/orders/${o.id}/status`, { estado: 'listo' });
                            load();
                          }}
                          data-testid={`kds-order-${o.numero}-ready`}
                        >
                          {t('mark_ready')}
                        </Button>
                      )}
                      {col.key === 'listo' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 border-white/20 text-white hover:bg-white/10"
                          onClick={async () => {
                            for (const it of (o.items || [])) {
                              if (it.estado === 'listo') {
                                await api.put(`/orders/${o.id}/items/${it.id}`, { estado: 'servido' });
                              }
                            }
                            await api.put(`/orders/${o.id}/status`, { estado: 'servido' });
                            load();
                          }}
                          data-testid={`kds-order-${o.numero}-served`}
                        >
                          {t('mark_served')}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
